'use client'

import { useSession } from 'next-auth/react'
import { useParams, usePathname } from 'next/navigation'
import React, {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { LogoutReasonNotifier } from '@maestro/commerce-blocks/components/logout-reason-notifier'
import {
  clearLogoutPending,
  isLogoutPending,
} from '@maestro/commerce-blocks/utils/logout.utils'
import { removeProfileUpdateBoxDismissed } from '@maestro/commerce-blocks/utils/profile-update-box.utils'
import { removeAllQueryIdTrackerData } from '@maestro/core/event/queryid-tracker.utils'

const MaestroSessionContext = createContext<{
  session: ReturnType<typeof useSession>['data'] | null
  isLoading: boolean
}>({ session: null, isLoading: true })

export function MaestroSessionProvider(props: {
  children: React.ReactNode
  disableSessionRefresh?: boolean
}) {
  const { children, disableSessionRefresh = false } = props
  const { data, update, status } = useSession()

  const pathname = usePathname()
  const pathnameRef = useRef(pathname)

  const params = useParams()
  const paramsKey = useMemo(() => {
    return JSON.stringify(params ?? {})
  }, [params])
  const paramsKeyRef = useRef(paramsKey)

  const [maestroSession, setMaestroSession] = useState(data)
  const [isLoading, setIsLoading] = useState(false)
  const hasTriggeredLogoutRef = useRef(false)

  useEffect(() => {
    setMaestroSession(data)
  }, [data])

  useEffect(() => {
    clearLogoutPending()
  }, [])

  const isLoadingSession = useMemo(() => status === 'loading', [status])

  useEffect(() => {
    if (disableSessionRefresh) {
      return
    }

    if (
      pathname === pathnameRef.current &&
      paramsKey === paramsKeyRef.current
    ) {
      return
    }
    pathnameRef.current = pathname
    paramsKeyRef.current = paramsKey

    // If user clicked manual logout — do NOT run update() and do NOT do sessionExpired redirect
    if (isLogoutPending()) {
      return
    }

    // Skip if logout is already in progress to avoid multiple redirects
    // (navigation changes can trigger this effect multiple times before hard redirect completes)
    if (hasTriggeredLogoutRef.current) {
      return
    }

    if (isLoading) {
      return
    }
    setIsLoading(true)

    update()
      .then((session) => {
        // sessionExpired should mean "expired automatically", not "I clicked logout"
        const sessionExpired =
          !session && !!maestroSession && status !== 'loading'
        if (!sessionExpired) {
          return
        }

        hasTriggeredLogoutRef.current = true

        // cleanup
        removeAllQueryIdTrackerData()
        if (maestroSession?.user?.hasProfileDifferences) {
          removeProfileUpdateBoxDismissed()
        }

        setMaestroSession(null)

        // Hard redirect to kill all effects and RSC race conditions
        window.location.assign('/logout?reason=session_expired')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [
    disableSessionRefresh,
    isLoading,
    maestroSession,
    paramsKey,
    pathname,
    status,
    update,
  ])

  return (
    <MaestroSessionContext.Provider
      value={{ session: maestroSession, isLoading: isLoadingSession }}
    >
      <Suspense fallback={null}>
        <LogoutReasonNotifier />
      </Suspense>
      {children}
    </MaestroSessionContext.Provider>
  )
}

export const useMaestroSession = () => {
  const context = useContext(MaestroSessionContext)
  if (!context) {
    throw new Error(
      'useMaestroSession must be used within a MaestroSessionProvider'
    )
  }
  return context
}
